import { canonicalCategory } from './legacy-category';
import { hasLiveSwitch } from './integration-toggle';
import type {
  AirlineCapabilityState,
  AirlineProviderService,
  AirlineProviderState,
  IntegrationDescriptor,
} from './types';

/**
 * THE AIRLINE PROVIDER AS ONE CARD — the pure model (contract `airline-air-ticketing.v1` §0).
 *
 * The airline provider (vendor `airline-service-provider`) is ONE service per tenant that may
 * give flights, passenger data and seats to sell. Two sources make its card, and they never mix:
 *
 *   · the AIRLINE (`GET /v1/providers/airline`) says whether a provider is connected, in which
 *     mode, and — capability by capability — whether it is available now or why not;
 *   · the CONTROL PLANE (the inventory rows of the vendor, one per service) holds the operator's
 *     switch: on, off, may it move, who moved it last.
 *
 * The airline reads the switch through a short cache, so right after a flip the two can disagree
 * for a few seconds. That is said ("not applied yet") and re-read a bounded number of times —
 * never papered over by inventing a capability state. When the airline cannot read the switch
 * at all (`null`: no control plane wired, or it did not answer) it keeps the service ON whatever
 * the operator chose — that is said as what it is, never as a delay that will pass.
 */

export const AIRLINE_PROVIDER_VENDOR = 'airline-service-provider';

/** The services, in the order the card lists them. */
export const AIRLINE_PROVIDER_SERVICES: readonly AirlineProviderService[] = [
  'flight-info',
  'passenger-data',
  'air-ticketing',
];

/** Where the card stands: we could not ask · nothing configured · a provider is there. */
export type AirlineConnection = 'unknown' | 'not_connected' | 'connected';

export function airlineConnection(state: AirlineProviderState | undefined): AirlineConnection {
  if (!state || !state.reachable) return 'unknown';
  return state.connected ? 'connected' : 'not_connected';
}

/** The provider's inventory row per service (the operator's switch), when declared. */
export function airlineRows(
  integrations: readonly IntegrationDescriptor[] | undefined,
): Partial<Record<AirlineProviderService, IntegrationDescriptor>> {
  const rows: Partial<Record<AirlineProviderService, IntegrationDescriptor>> = {};
  for (const d of integrations ?? []) {
    if (d.vendor !== AIRLINE_PROVIDER_VENDOR) continue;
    const service = canonicalCategory(d.category) as AirlineProviderService;
    if (AIRLINE_PROVIDER_SERVICES.includes(service)) rows[service] ??= d;
  }
  return rows;
}

/** All on · all off · some of each · nothing to switch. */
export type MasterSwitchState = 'on' | 'off' | 'mixed' | 'none';

export function masterSwitchState(rows: Partial<Record<AirlineProviderService, IntegrationDescriptor>>): MasterSwitchState {
  const switchable = Object.values(rows).filter((d) => d && hasLiveSwitch(d));
  if (switchable.length === 0) return 'none';
  const on = switchable.filter((d) => d.enabled).length;
  if (on === switchable.length) return 'on';
  return on === 0 ? 'off' : 'mixed';
}

/**
 * What pressing the master switch may do: all on → all off; all off → all on; a mix → EITHER,
 * all off first — from a mix it must still work as the kill switch, without turning on first a
 * service the operator had switched off (live ticket sales, say).
 */
export function masterTargets(state: MasterSwitchState): boolean[] {
  if (state === 'on') return [false];
  if (state === 'off') return [true];
  return state === 'mixed' ? [false, true] : [];
}

/**
 * Can the master switch do anything toward `target`? Some service is not there yet AND the
 * control plane lets at least one of those move. A service it refuses is reported after the
 * action, by name and reason — it does not lock the whole provider.
 */
export function masterCanMove(
  rows: Partial<Record<AirlineProviderService, IntegrationDescriptor>>,
  target: boolean,
): boolean {
  return Object.values(rows).some((d) => d && hasLiveSwitch(d) && d.enabled !== target && d.toggle?.allowed === true);
}

/**
 * The services whose switch the airline has not applied yet: the control plane says one thing,
 * the airline (through its cache of seconds) still reads the other. Only a reading the airline
 * HAS (`true`/`false`) can lag; a `null` one is {@link unreadServices}, an absent one says nothing.
 */
export function pendingServices(
  state: AirlineProviderState | undefined,
  rows: Partial<Record<AirlineProviderService, IntegrationDescriptor>>,
): AirlineProviderService[] {
  if (airlineConnection(state) !== 'connected') return [];
  return AIRLINE_PROVIDER_SERVICES.filter((service) => {
    const row = rows[service];
    if (!row || !hasLiveSwitch(row)) return false;
    const airlineReads = state!.switches[service];
    return typeof airlineReads === 'boolean' && airlineReads !== row.enabled;
  });
}

/**
 * The services whose switch the airline CANNOT read (`null`: it has no control plane wired, or
 * the control plane did not answer it). It keeps them ON whatever the switch says — not a delay
 * that re-reading fixes, so it is never {@link pendingServices}.
 */
export function unreadServices(
  state: AirlineProviderState | undefined,
  rows: Partial<Record<AirlineProviderService, IntegrationDescriptor>>,
): AirlineProviderService[] {
  if (airlineConnection(state) !== 'connected') return [];
  return AIRLINE_PROVIDER_SERVICES.filter((service) => {
    const row = rows[service];
    return !!row && hasLiveSwitch(row) && state!.switches[service] === null;
  });
}

/**
 * What disagrees right now, as a key: each pending service with the switch the operator set and
 * when. `''` when nothing does. A new flip — of the same service or another — is a new key, so it
 * gets its own bounded catch-up instead of inheriting one already spent.
 */
export function pendingKey(
  state: AirlineProviderState | undefined,
  rows: Partial<Record<AirlineProviderService, IntegrationDescriptor>>,
): string {
  return pendingServices(state, rows)
    .map((service) => {
      const row = rows[service]!;
      return `${service}:${row.enabled ? 'on' : 'off'}@${row.toggle?.lastChange?.at ?? ''}`;
    })
    .join('|');
}

/** The capabilities of one service, in contract order. */
export function serviceCapabilities(
  state: AirlineProviderState | undefined,
  service: AirlineProviderService,
): AirlineCapabilityState[] {
  return (state?.capabilities ?? []).filter((c) => c.service === service);
}

/**
 * The reason every capability of a service shares, when they all share one ("apagado" for a
 * switched-off service, "no conectado" for a missing provider) — said once for the service
 * instead of once per capability. `undefined` when any is available or the reasons differ.
 */
export function sharedReason(capabilities: readonly AirlineCapabilityState[]): AirlineCapabilityState['reason'] {
  const first = capabilities[0];
  if (!first || first.available) return undefined;
  return capabilities.every((c) => !c.available && c.reason === first.reason) ? first.reason : undefined;
}
